import GeneralEditorView from "./generalEditor.view";
import UnitsEditorView from "./unitsEditor.view";
import React, {useEffect, useRef, useState} from "react";
import BuildingsEditorView from "./buildingsEditor.view";
import LocalizationEditorView from "./localizationEditor.view";
import {HTML5Backend} from "react-dnd-html5-backend";
import {DndProvider} from "react-dnd";
import ImagesEditorView from "./imagesEditor.view";
import ArchiveView from "./archive.view";
import TechTreeEditorView from "../techTree/techTreeEditor.view";
import {useApp} from "../../app.context";
import {useIntegrator} from "../../integrator.context";
import SoundsEditorView from "./soundsEditor.view";
import StylesEditorView from "./stylesEditor.view";
import TilesEditorView from "./tilesEditor.view";
import ResourcesEditor from "./resources.editor";
import MapEditorView from "../maps/mapEditor.view";
import {useAccess} from "../../utils/useAccess";
import WelcomeView from "./welcome.view";
import BuildingRewardsEditor from "./buildingRewards.editor";
import TechEditor from "./tech.editor";
import OrdersEditor from "./orders.editor";
import UnitUpgradesEditorView from "./unitUpgradesEditor.view";
import AttributesEditor from "./attributes.editor";
import SettingsEditorView from "./settingsEditor.view";
import UsersEditorView from "./usersEditor.view";
import AvatarEditorView from "../avatar/avatarEditor.view";
import SaveButton from "../../elements/saveButton";
import LivePreviewControl from "../../elements/livePreviewControl";
import {SyncHiddenUnitAttributesCommand} from "../../commands/syncHiddenUnitAttributes.command";
import {MetadataCollection} from "../../commands/metadataValue.command";

enum EditorState {
    Welcome = "Welcome",
    Gameplay = "Gameplay",
    Assets = "Assets",
    General = "General",
    TechTree = "Tech Tree",
    Units = "Units",
    Buildings = "Buildings",
    Tiles = "Tiles",
    Resources = "Resources",
    Maps = "Maps",
    Rewards = "City Rewards",
    UnitUpgrades = "Unit Upgrades",
    Attributes = "Attributes",
    Tech = "Tech",
    Orders = "Orders",
    Images = "Images",
    Sounds = "Sounds",
    Localization = "Localization",
    Styles = "Styles",
    Avatar = "Avatar",
    Archive = "Archive",
    CombatSimulation = "Combat Simulation",
    Settings = "Settings",
    Users = "Users",
}

function EditorView() {
    const {totalChanges, branch, getBranchVersionNumber, goBack, goForward, canGoBack, canGoForward, executeCommand, state: integratorState} = useIntegrator();
    const {setLoading, setViewBranch, exportRemoteConstants, setOnNavigateToLocalization, setOnNavigateToUnit, setOnNavigateToBuilding, setOnNavigateToImage, setOnNavigateToTile, setOnNavigateToResource, setOnNavigateToMetadata, attributeManifest} = useApp();
    const {canManageUsers, canPreview, canExportDev, canEditAvatars} = useAccess();

    // Auto-seed hidden unit attributes when both the manifest and a branch are
    // loaded. The command is idempotent — if every Hidden id is already in
    // state.unitAttributes it returns false and the editor stays clean. When
    // it does add entries, it goes through the normal command flow so the
    // branch shows as dirty until saved.
    //
    // Depends on state.unitAttributes (not the whole state) because in-place
    // mutations via executeCommand keep the array reference stable; only a
    // branch reload replaces it. That keeps this effect from re-running on
    // every unrelated edit.
    useEffect(() => {
        if (!attributeManifest) return;
        // Automatic normalization — exempt from the read-only guard so viewers
        // still see correctly-seeded hidden attributes.
        executeCommand(new SyncHiddenUnitAttributesCommand(attributeManifest), {system: true});
        // executeCommand is deliberately omitted: its identity isn't stable
        // across renders and the deps above (see comment) are the real triggers.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [attributeManifest, integratorState.unitAttributes]);
    const [state, setState] = useState<string>(loadCachedState());
    // Sidebar visibility, persisted across reloads like the selected tab.
    const SIDEBAR_HIDDEN_KEY = 'rift.editor.sidebarHidden';
    const [sidebarHidden, _setSidebarHidden] = useState<boolean>(
        () => localStorage.getItem(SIDEBAR_HIDDEN_KEY) === 'true'
    );
    const setSidebarHidden = (value: boolean) => {
        localStorage.setItem(SIDEBAR_HIDDEN_KEY, String(value));
        _setSidebarHidden(value);
    };
    const isPopstateRef = useRef(false);

    useEffect(() => {
        if (totalChanges > 0) {
            window.onbeforeunload = () => true;
        } else {
            window.onbeforeunload = null;
        }
        return () => { window.onbeforeunload = null; };
    }, [totalChanges]);

    function selectState(newState: string) {
        if (newState === state) return;
        if (!isPopstateRef.current) {
            window.history.pushState({editorState: newState}, '');
        }
        setState(newState);
        cacheState(newState);
    }

    useEffect(() => {
        window.history.replaceState({editorState: state}, '');

        function onPopState(e: PopStateEvent) {
            const prev = e.state?.editorState;
            if (prev) {
                isPopstateRef.current = true;
                setState(prev);
                cacheState(prev);
                isPopstateRef.current = false;
            }
        }
        window.addEventListener('popstate', onPopState);
        return () => window.removeEventListener('popstate', onPopState);
        // Mount-only by design: `state` is only read to seed the initial
        // history entry; the listener must not be re-registered per tab switch.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setOnNavigateToLocalization((_key: string) => {
            selectState(EditorState.Localization);
        });
        setOnNavigateToUnit(() => {
            selectState(EditorState.Units);
        });
        setOnNavigateToBuilding(() => {
            selectState(EditorState.Buildings);
        });
        setOnNavigateToImage(() => {
            selectState(EditorState.Images);
        });
        setOnNavigateToTile(() => {
            selectState(EditorState.Tiles);
        });
        setOnNavigateToResource(() => {
            selectState(EditorState.Resources);
        });
        setOnNavigateToMetadata((collection) => {
            // Route to the tab that edits this metadata collection.
            switch (collection) {
                case MetadataCollection.Units:
                case MetadataCollection.Buildings:
                    selectState(EditorState.Attributes); break;
                case MetadataCollection.TechActions:
                case MetadataCollection.TechPassives:
                    selectState(EditorState.Tech); break;
                case MetadataCollection.Rewards:
                    selectState(EditorState.Rewards); break;
            }
        });
        return () => {
            setOnNavigateToLocalization(undefined);
            setOnNavigateToUnit(undefined);
            setOnNavigateToBuilding(undefined);
            setOnNavigateToImage(undefined);
            setOnNavigateToTile(undefined);
            setOnNavigateToResource(undefined);
            setOnNavigateToMetadata(undefined);
        };
        // Re-registers on `state` so the callbacks close over the current tab;
        // the context setters and selectState are not stable identities and
        // would only add re-registration noise as deps.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state]);

    async function doExport(prod: boolean) {
        setLoading(true);
        await exportRemoteConstants(prod);
        setLoading(false);
    }

    return (
        <DndProvider backend={HTML5Backend}>
            <div className={'layout vertical'} style={{height: '100vh'}}>
                <div className={'layout horizontal'} style={{padding: 8,
                    // When the sidebar is collapsed, widen the left gutter so the
                    // 28px absolutely-positioned restore button isn't overlapped
                    // by the content panel (which otherwise starts at the page padding).
                    paddingLeft: sidebarHidden ? 30 : 8,
                    gap: 8, flexGrow: 1, minHeight: 0, position: 'relative'}}>
                    {sidebarHidden &&
                        <button title={'Show sidebar'} className={'panel-toggle'}
                                onClick={() => setSidebarHidden(false)}
                                style={{position: 'absolute', left: 0, top: 8, border: 'none', margin: 0}}>
                            {/* Mirror of the "Hide sidebar" chevron (points the
                                opposite way to indicate expand), borderless. */}
                            <span className={'fa-solid fa-chevron-right'}/>
                        </button>}
                    <div className={'layout vertical stretch'}
                         style={{minHeight: 0, flexShrink: 0, minWidth: 184, display: sidebarHidden ? 'none' : 'flex'}}>
                        {/* Tabs scroll when the window is short; justify-content
                            must be flex-start (the `center` class centers the
                            main axis, which makes the top of an overflowing
                            scroll container unreachable). */}
                        <div className={'layout vertical center'}
                             style={{gap: 10, overflowY: 'auto', minHeight: 0, justifyContent: 'flex-start'}}>
                            <div className={'layout horizontal center stretch'} style={{width: '100%', marginBottom: 10}}>
                                <button title={'Hide sidebar'} className={'panel-toggle'} onClick={() => setSidebarHidden(true)}>
                                    <span className={'fa-solid fa-chevron-left'}/>
                                </button>
                                <p className={'text header'} style={{flexGrow: 1, textAlign: 'center'}}>Rift Editor</p>
                                {/* Spacer mirroring the hide button's width so the
                                    title stays centred across the full sidebar width. */}
                                <span style={{width: 28, flexShrink: 0}}/>
                            </div>
                            <TitleText text={'Welcome'} icon={'fa-solid fa-hand-wave'}
                                       state={EditorState.Welcome} selectedState={state} selectState={selectState}/>
                            <div className={'layout vertical'} style={{width: '100%'}}>
                                <StateButton state={EditorState.General} file={'general.md'} selectedState={state}
                                             selectState={selectState}/>
                                <StateButton state={EditorState.Settings} file={'settings.md'} selectedState={state}
                                             selectState={selectState}/>
                                {canManageUsers &&
                                    <StateButton state={EditorState.Users} file={'users.md'} selectedState={state}
                                                 selectState={selectState}/>}
                            </div>
                            <TitleText text={'Gameplay'} icon={'fa-solid fa-gamepad'}
                                       state={EditorState.Gameplay} selectedState={state} selectState={selectState}/>
                            <div className={'layout vertical'} style={{width: '100%'}}>
                                <StateButton state={EditorState.TechTree} file={'tech_tree.md'} selectedState={state}
                                             selectState={selectState}/>
                                <StateButton state={EditorState.Units} file={'units.md'} selectedState={state}
                                             selectState={selectState}/>
                                <StateButton state={EditorState.Buildings} file={'buildings.md'} selectedState={state}
                                             selectState={selectState}/>
                                <StateButton state={EditorState.Tiles} file={'tiles.md'} selectedState={state}
                                             selectState={selectState}/>
                                <StateButton state={EditorState.Resources} file={'resources.md'} selectedState={state}
                                             selectState={selectState}/>
                                <StateButton state={EditorState.Maps} file={'maps.md'} selectedState={state}
                                             selectState={selectState}/>
                                <StateButton state={EditorState.Rewards} file={'rewards.md'} selectedState={state}
                                             selectState={selectState}/>
                                <StateButton state={EditorState.UnitUpgrades} file={'unit_upgrades.md'} selectedState={state}
                                             selectState={selectState}/>
                                <StateButton state={EditorState.Attributes} file={'attributes.md'} selectedState={state}
                                             selectState={selectState}/>
                                <StateButton state={EditorState.Tech} file={'tech.md'} selectedState={state}
                                             selectState={selectState}/>
                                <StateButton state={EditorState.Orders} file={'orders.md'} selectedState={state}
                                             selectState={selectState}/>
                            </div>
                            <TitleText text={'Assets'} icon={'fa-solid fa-image'}
                                       state={EditorState.Assets} selectedState={state} selectState={selectState}/>
                            <div className={'layout vertical'} style={{width: '100%'}}>
                                <StateButton state={EditorState.Images} file={'images.md'} selectedState={state}
                                             selectState={selectState}/>
                                <StateButton state={EditorState.Sounds} file={'sounds.md'} selectedState={state}
                                             selectState={selectState}/>
                                <StateButton state={EditorState.Localization} file={'localization.md'} selectedState={state}
                                             selectState={selectState}/>
                                <StateButton state={EditorState.Styles} file={'styles.md'} selectedState={state}
                                             selectState={selectState}/>
                                {canEditAvatars &&
                                    <StateButton state={EditorState.Avatar} file={'avatar.md'} selectedState={state}
                                                 selectState={selectState}/>}
                                <StateButton state={EditorState.Archive} file={'archive.md'} selectedState={state}
                                             selectState={selectState}/>
                            </div>
                        </div>
                        {/* Branch admin: pinned bottom-left, never crushed by
                            the tabs above (which scroll instead). */}
                        <div className={'layout vertical gap'} style={{flexShrink: 0, paddingTop: 10}}>
                            {canPreview && <LivePreviewControl/>}
                            <div className={'layout horizontal gap'}>
                                <button onClick={goBack} style={{opacity: `${canGoBack() ? 1 : 0.5}`}}>
                                    <span className={'fa-solid fa-undo'}/>
                                </button>
                                <button onClick={goForward} style={{opacity: `${canGoForward() ? 1 : 0.5}`}}>
                                    <span className={'fa-solid fa-redo'}/>
                                </button>
                                {/* Branch switch/compare is a read-only op, open to every
                                    role; create/delete are gated inside the modal. */}
                                <button className={'flex-grow'} onClick={() => setViewBranch(true)}>
                                    {branch}
                                </button>
                            </div>
                            {/* Export block: visible only when the current role can persist
                                the current branch to dev/test (Tester+, and Admin+ for
                                Master). Live export lives in the Settings tab (Admin+). */}
                            {canExportDev &&
                                <>
                                    <div className={'layout horizontal stretch'}>
                                        <p className={totalChanges > 0 ? 'warning-text' : 'muted-text'}>{totalChanges > 0 ? `Unsaved` : `No Changes`}</p>
                                        <div style={{minWidth: 6}}/>
                                        <p className={'normal-text'}>{`Version ${getBranchVersionNumber(branch)}`}</p>
                                    </div>
                                    <button className={`export-button ${totalChanges > 0 && 'active'}`}
                                            onClick={() => doExport(false)}>
                                        Export
                                    </button>
                                    <SaveButton/>
                                </>
                            }
                        </div>
                    </div>
                    <div style={paneStyle(state === EditorState.Welcome)}><WelcomeView/></div>
                    <div style={paneStyle(state === EditorState.Gameplay)}>
                        <SectionPlaceholder icon={'fa-solid fa-gamepad'} title={'Gameplay'}
                                            body={'The rules of play. Tech Tree gates assets behind research; Units and Buildings define entities and their stats; Tiles and Resources define terrain and what spawns on it; City Rewards authors city level-up pools; Unit Upgrades the attack and defence upgrades researched at the Forge; Attributes and Tech define the markers and tech actions/passives that entities and nodes reference. Pick a tab below.'}/>
                    </div>
                    <div style={paneStyle(state === EditorState.Assets)}>
                        <SectionPlaceholder icon={'fa-solid fa-image'} title={'Assets'}
                                            body={'Presentation. Images holds every sprite with its pivot and scale; Sounds holds music and effects; Localization holds the per-language text; Archive keeps displaced files for restore. Pick a tab below.'}/>
                    </div>
                    <div style={paneStyle(state === EditorState.General)}><GeneralEditorView/></div>
                    <div style={paneStyle(state === EditorState.TechTree)}><TechTreeEditorView/></div>
                    <div style={paneStyle(state === EditorState.Units)}><UnitsEditorView/></div>
                    <div style={paneStyle(state === EditorState.Buildings)}><BuildingsEditorView/></div>
                    <div style={paneStyle(state === EditorState.Tiles)}><TilesEditorView/></div>
                    <div style={paneStyle(state === EditorState.Resources)}><ResourcesEditor/></div>
                    <div style={paneStyle(state === EditorState.Maps)}><MapEditorView/></div>
                    <div style={paneStyle(state === EditorState.Rewards)}><BuildingRewardsEditor/></div>
                    <div style={paneStyle(state === EditorState.UnitUpgrades)}><UnitUpgradesEditorView/></div>
                    <div style={paneStyle(state === EditorState.Attributes)}><AttributesEditor/></div>
                    <div style={paneStyle(state === EditorState.Tech)}><TechEditor/></div>
                    <div style={paneStyle(state === EditorState.Orders)}><OrdersEditor/></div>
                    <div style={paneStyle(state === EditorState.Images)}><ImagesEditorView/></div>
                    <div style={paneStyle(state === EditorState.Sounds)}><SoundsEditorView/></div>
                    <div style={paneStyle(state === EditorState.Styles)}><StylesEditorView/></div>
                    <div style={paneStyle(state === EditorState.Avatar)}><AvatarEditorView/></div>
                    <div style={paneStyle(state === EditorState.Localization)}><LocalizationEditorView/></div>
                    <div style={paneStyle(state === EditorState.Archive)}><ArchiveView/></div>
                    <div style={paneStyle(state === EditorState.Settings)}><SettingsEditorView/></div>
                    <div style={paneStyle(state === EditorState.Users)}><UsersEditorView/></div>
                </div>
            </div>
        </DndProvider>
    )
}

// Content panes: real flex boxes (not display:contents) so every view is
// pinned to the viewport-height row and scrolls internally when its content
// is taller. Views with their own scroll areas are unaffected (they already
// constrain their height); views without get the wrapper's overflow.
function paneStyle(visible: boolean): React.CSSProperties {
    if (!visible) return {display: 'none'};
    return {
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        minWidth: 0,
        minHeight: 0,
        overflow: 'auto',
    };
}

function StateButton(props: {state: EditorState, file: string | undefined, selectedState: string, selectState(state: string): void}) {
    const {setMarkdownFile} = useApp();
    const selected = props.state === props.selectedState;

    return <button
                   className={`layout horizontal center stretch gap bar-state ${selected ? 'bar-state-active' : 'bar-state-inactive'}`}
                   // Indented under their section headings; width compensates so
                   // the right edge still lines up with the headings.
                   style={{width: 'calc(100% - 24px)', marginLeft: 24}}
                   onClick={() => {
                       if (selected && props.file) {
                           setMarkdownFile(props.file);
                       } else {
                           props.selectState(props.state);
                       }
                   }}>
        {props.state}
        {selected && props.file &&
            <span className={'fa-solid fa-square-info'}/>
        }
    </button>
}

// Section heading. When given a `state`, the heading is clickable and selects
// that state (showing its overview pane), highlighting like a tab when active.
function TitleText(props: {text: string, icon: string, state?: EditorState, selectedState?: string, selectState?(state: string): void}) {
    const clickable = props.state !== undefined && props.selectState !== undefined;
    const selected = clickable && props.state === props.selectedState;
    return <p className={'layout horizontal'}
              onClick={clickable ? () => props.selectState!(props.state!) : undefined}
              style={{
                  width: '100%', fontWeight: '600', gap: 10, alignItems: 'center',
                  cursor: clickable ? 'pointer' : undefined,
                  color: selected ? 'var(--tint-color)' : undefined,
              }}>
        <span className={props.icon}/>
        {props.text}
    </p>
}

// Placeholder pane for a section heading that doesn't yet have a dedicated
// overview view (Gameplay / Assets).
function SectionPlaceholder(props: {icon: string, title: string, body: string}) {
    return <div className={'layout vertical center'} style={{flexGrow: 1, gap: 12, padding: 40, textAlign: 'center'}}>
        <span className={props.icon} style={{fontSize: 48, opacity: 0.4}}/>
        <p className={'text header'}>{props.title}</p>
        <p className={'muted-text'} style={{maxWidth: 460}}>{props.body}</p>
    </div>
}

function loadCachedState(): string {
    const value = localStorage.getItem('features-state');
    if (value === null) return EditorState.Welcome;
    // Tab was renamed from "Rewards" to "City Rewards"; migrate cached value
    // so users mid-session don't land on a no-op string.
    if (value === "Rewards") return EditorState.Rewards;
    return value;
}

function cacheState(state: string) {
    localStorage.setItem('features-state', state);
}

export default EditorView;